import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import {
  EivPayerDeltaIcon,
  EivPayerAetnaIcon,
  EivPayerCignaIcon,
  EivPayerDxcIcon,
  EivPayerMetlifeIcon,
  EivPayerUhcIcon,
} from '@frontend/self-serve#svg';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { EIVDashboardTrialWidgetTrackingIds } from '../../../../constants/tracking-ids';

const PayersListMap = [
  {
    name: i18next.t('Delta Dental', { ns: 'featurePromotion>>weave-insurance-eligibility' }),
    icon: EivPayerDeltaIcon,
  },
  {
    name: i18next.t('Aetna', { ns: 'featurePromotion>>weave-insurance-eligibility' }),
    icon: EivPayerAetnaIcon,
  },
  {
    name: i18next.t('Cigna', { ns: 'featurePromotion>>weave-insurance-eligibility' }),
    icon: EivPayerCignaIcon,
  },
  {
    name: i18next.t('MetLife', { ns: 'featurePromotion>>weave-insurance-eligibility' }),
    icon: EivPayerMetlifeIcon,
  },
  {
    name: i18next.t('UHC', { ns: 'featurePromotion>>weave-insurance-eligibility' }),
    icon: EivPayerUhcIcon,
  },
  {
    name: i18next.t('DXC', { ns: 'featurePromotion>>weave-insurance-eligibility' }),
    icon: EivPayerDxcIcon,
  },
];

const TOTAL_CONNECTED_PAYERS = 11;

export const PayersList: FC = () => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });
  const { navigate: navigateToSettings } = useSettingsNavigate();

  const isLargeScreen = useMatchMedia({ maxWidth: breakpoints.large.max });
  const isMediumScreen = useMatchMedia({ maxWidth: breakpoints.medium.max });
  const isSmallScreen = useMatchMedia({ maxWidth: breakpoints.small.max });

  const payerCountToDisplay = useMemo(() => {
    if (isSmallScreen) {
      return 0;
    } else if (isMediumScreen) {
      return 2;
    } else if (isLargeScreen) {
      return 4;
    }
    return 6;
  }, [isSmallScreen, isMediumScreen, isLargeScreen]);

  return (
    <div css={defaultStyles.container}>
      {PayersListMap.slice(0, payerCountToDisplay).map((payer) => {
        const IconComponent = payer.icon;
        return (
          <div key={payer.name} css={defaultStyles.payerName}>
            <IconComponent css={{ width: 20, height: 20 }} />
            <Text size='small' weight='bold'>
              {payer.name}
            </Text>
          </div>
        );
      })}
      <div css={defaultStyles.payerName}>
        <Icon name='shield-small' size={20} css={{ color: theme.colors.neutral50 }} />
        <Text size='small' color='subdued' weight='bold'>
          {payerCountToDisplay > 0
            ? t('{{count}} Others', { count: TOTAL_CONNECTED_PAYERS - payerCountToDisplay })
            : t('{{count}} Payers Available', { count: TOTAL_CONNECTED_PAYERS })}
        </Text>
      </div>
      <Button
        variant='primary'
        onClick={() => navigateToSettings({ to: '/insurance/payer-connection-management' })}
        trackingId={EIVDashboardTrialWidgetTrackingIds.startTrialAction}
      >
        {t('Connect Payers')}
      </Button>
    </div>
  );
};

const defaultStyles = {
  container: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
    justify-content: space-around;
    @media (max-width: ${breakpoints.small.max}px) {
      flex-direction: column;
      button {
        width: 100%;
      }
    }
  `,
  payerName: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
};
